import { useEffect, useMemo, useState } from 'react'
import type { MouseEvent } from 'react'
import {
  AlertOctagon, Check, History, ImagePlus, Loader2, MapPin, Pencil, Play, ShieldCheck, X,
} from 'lucide-react'
import type { Breakdown, BreakdownLogEntry, BreakdownReconcile } from '../lib/types'
import {
  editBreakdown, fetchBreakdownLog, fetchBreakdowns, reconcileBreakdown, uploadBreakdownPhoto,
} from '../lib/breakdownApi'
import {
  reportCard, reportCardPadSm, reportCardSub, reportCardTitle, reportEyebrow,
  reportPill, reportTh, reportThead, rowEntrance,
} from '../reports/ui'
import { CountUp } from '../ui/primitives'
import { VehicleSearchField } from '../reports/VehicleSearchField'
import { BreakdownSnapshot } from './BreakdownSnapshot'
import { VehicleCard } from '../cc/VehicleCard'
import { BreakdownQuickResolve } from './BreakdownQuickResolve'
import { CATEGORIES, SEVERITIES } from './BreakdownForm'
import { useSession } from '../auth/useSession'
import { useFleet } from '../state/store'
import { localInputValueToMs, msToLocalInputValue } from '../lib/localDateTime'
import { pollWhileVisible } from '../lib/pollWhileVisible'

/** The reconcile icon's colour IS the persisted status -- 'unchecked' reads the same as
 *  the default slate everything else uses, deliberately: it is not a confirmed problem,
 *  just not yet looked at, and should not compete visually with a real mismatch. */
const RECONCILE_STATUS_COLOR: Record<BreakdownLogEntry['reconcile_status'], string> = {
  unchecked: 'text-slate-400',
  matches: 'text-emerald-600',
  mismatch: 'text-amber-600',
  inconclusive: 'text-slate-400',
}
const RECONCILE_STATUS_LABEL: Record<BreakdownLogEntry['reconcile_status'], string> = {
  unchecked: 'not yet checked',
  matches: 'reconciles',
  mismatch: 'does not reconcile — needs review',
  inconclusive: 'no comparison available',
}

/**
 * The whole breakdown story in one place: who is down right now (with a one-click
 * resolve, editable reported time, and photos), and every incident that has ever
 * happened, with turn-around time so a pattern (one vehicle, one workshop, one fault)
 * is visible instead of buried in per-vehicle history.
 *
 * Lives next to Fleet / Alerts / Actionables / Trips, not inside the Alerts report --
 * this is operational state a supervisor checks the same way they check who's on the
 * road, not a report someone runs at the end of a shift.
 */
export function BreakdownsPanel({
  onSelectVehicle,
  onReplay,
}: {
  /** Click a card's plate/status header -- "show detail and take me to where it is."
   *  Selecting the vehicle already opens its drawer with the full breakdown status this
   *  panel doesn't repeat (reported at, resolve, note); this just also flies the map
   *  there, since a broken-down truck the supervisor cannot picture is not much use.
   *  The fallback position is where THIS incident was marked down -- live telemetry may
   *  have nothing current for a vehicle that is, after all, broken down. */
  onSelectVehicle?: (vehicleId: string, fallback?: { lat: number; lon: number }) => void
  /** Replay this incident on the map: every geotag from when it was reported through to
   *  the next different corridor site it reaches -- not the vehicle's latest unrelated
   *  completed leg, which is what VehicleDrawer's own replay picks. */
  onReplay?: (breakdownId: number) => void
}): JSX.Element {
  const session = useSession()
  // Editing and reconcile both change or reveal nothing to a driver/dispatcher role -- the
  // server enforces this independently (_require_admin_raw_data), this just keeps the
  // affordance from appearing for someone who would only get a 403 from it.
  const isAdmin = session.status === 'signed-in' && session.session.is_admin !== false
  const [active, setActive] = useState<Breakdown[]>([])
  const [activeLoaded, setActiveLoaded] = useState(false)
  const [log, setLog] = useState<BreakdownLogEntry[]>([])
  const [medianTat, setMedianTat] = useState<number | null>(null)
  const [resolvedCount, setResolvedCount] = useState(0)
  const [logLoaded, setLogLoaded] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  // Client-side only -- both lists are already fully fetched (log capped at 150, active is
  // never large), so a round trip for this would just add latency for no benefit.
  const [vehicleId, setVehicleId] = useState('')
  const plateQ = vehicleId.trim().toLowerCase()
  const filteredActive = useMemo(
    () => (plateQ ? active.filter((b) => b.vehicle_id.toLowerCase().includes(plateQ)) : active),
    [active, plateQ],
  )
  const filteredLog = useMemo(
    () => (plateQ ? log.filter((inc) => inc.vehicle_id.toLowerCase().includes(plateQ)) : log),
    [log, plateQ],
  )
  // Paginated, not the old capped-height inner scroll -- see the outer div's own comment
  // for why that scroll container had to go. 150 rows in one un-paginated table was also
  // just a lot to scan past to find one incident.
  const LOG_PAGE_SIZE = 20
  const [page, setPage] = useState(0)
  useEffect(() => { setPage(0) }, [vehicleId, dateFrom, dateTo])
  const logPageCount = Math.max(1, Math.ceil(filteredLog.length / LOG_PAGE_SIZE))
  const logPage = Math.min(page, logPageCount - 1)
  const pagedLog = filteredLog.slice(logPage * LOG_PAGE_SIZE, (logPage + 1) * LOG_PAGE_SIZE)
  const breakdownEdit = useFleet((s) => s.breakdownEdit)
  const liveBreakdowns = useFleet((s) => s.breakdowns)

  const [reconcileById, setReconcileById] = useState<Map<number, BreakdownReconcile>>(new Map())
  const [reconcileErrById, setReconcileErrById] = useState<Map<number, string>>(new Map())
  const [reconcilingIds, setReconcilingIds] = useState<Set<number>>(new Set())
  const [reconcilingAll, setReconcilingAll] = useState(false)

  async function reconcileOne(breakdownId: number): Promise<void> {
    setReconcilingIds((cur) => new Set(cur).add(breakdownId))
    setReconcileErrById((cur) => { const m = new Map(cur); m.delete(breakdownId); return m })
    try {
      const result = await reconcileBreakdown(breakdownId)
      setReconcileById((cur) => new Map(cur).set(breakdownId, result))
    } catch (e) {
      setReconcileErrById((cur) => new Map(cur).set(
        breakdownId, e instanceof Error ? e.message : 'could not reconcile'))
    } finally {
      setReconcilingIds((cur) => { const s = new Set(cur); s.delete(breakdownId); return s })
    }
  }

  // Bounded concurrency, not Promise.all(everything) -- the log can hold up to 150 rows,
  // and reconcile is a real read+haversine per row, not a cache lookup.
  async function reconcileAll(): Promise<void> {
    setReconcilingAll(true)
    const ids = filteredLog.map((inc) => inc.breakdown_id)
    const CONCURRENCY = 6
    let next = 0
    async function worker(): Promise<void> {
      while (next < ids.length) {
        const id = ids[next]!
        next += 1
        await reconcileOne(id)
      }
    }
    try {
      await Promise.all(Array.from({ length: Math.min(CONCURRENCY, ids.length) }, worker))
    } finally {
      setReconcilingAll(false)
    }
  }

  const loadActive = (): void => {
    fetchBreakdowns()
      .then((rows) => { setActive(rows); setErr(null) })
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)))
      .finally(() => setActiveLoaded(true))
  }
  const loadLog = (from: string, to: string): void => {
    fetchBreakdownLog(150, { from: from || undefined, to: to || undefined })
      .then(({ incidents, medianTatMin, resolvedCount: rc }) => {
        setLog(incidents); setMedianTat(medianTatMin); setResolvedCount(rc)
      })
      .catch(() => { /* the log is a secondary view; the active list above is what matters live */ })
      .finally(() => setLogLoaded(true))
  }

  // 20s, not eliminated by the `liveBreakdowns` SSE effect below -- this IS the heal-poll
  // that effect exists on top of, not duplicate of it. serving runs multiple replicas
  // with no cross-pod SSE fan-out (see that effect's own comment), so this poll is the
  // only thing that reaches a tab connected to a different pod than the one that
  // processed an edit. A backgrounded tab has no supervisor watching it either way.
  useEffect(() => pollWhileVisible(loadActive, 20_000), [])

  // Re-loads whenever the date filter changes, and every 20s while a filter is not the
  // most recent thing the operator touched -- same live-refresh cadence as the active
  // list above, just scoped to whatever window is currently selected.
  useEffect(() => pollWhileVisible(() => loadLog(dateFrom, dateTo), 20_000), [dateFrom, dateTo])

  // Patch in place on ANY admin's edit, not just this tab's own -- LogRow's own save()
  // already updates its row optimistically for the editor; this is what makes that same
  // edit show up on every OTHER open console without waiting for the next 20s poll.
  useEffect(() => {
    if (!breakdownEdit) return
    setLog((cur) => cur.map((x) => (
      x.breakdown_id === breakdownEdit.breakdown_id ? { ...x, ...breakdownEdit } : x
    )))
    // Hub/Distance are derived server-side from position -- a lat/lon correction needs a
    // real refetch to recompute them, on every open tab, not just the editor's own.
    if (breakdownEdit.lat != null || breakdownEdit.lon != null) loadLog(dateFrom, dateTo)
  }, [breakdownEdit])

  // THE BUG THIS CLOSES: mark/update/clear (VehicleDrawer's quick-resolve, the mark-down
  // form, this table's own edit) only ever broadcast a plain 'breakdown' SSE event, never
  // 'breakdown_edited' -- so neither of the two effects above ever fired for them, and
  // "Currently held up" / "All breakdowns" sat on whatever they last polled until the next
  // 20s tick. serving runs multiple replicas with no cross-pod SSE fan-out (see
  // _active_breakdowns_from_db's own comment) -- an edit that landed on a different pod
  // than this browser's SSE connection never reaches it at all -- so the store's live
  // `breakdowns` map is used here ONLY as a same-pod REFETCH TRIGGER, never as the data
  // itself: every branch below still reads from Postgres, the one place every replica
  // agrees. This closes the common case (the browser that just made the edit is normally
  // on the same pod that broadcast it) instantly, on top of the 20s poll that already
  // covers the cross-pod case.
  useEffect(() => {
    loadActive()
    loadLog(dateFrom, dateTo)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveBreakdowns])

  return (
    // No overflow/padding here -- DetailWorkspace's own tab-content wrapper already scrolls
    // and pads the whole page. A second overflow-auto here nested inside an identically-
    // sized parent was the "scroll is broken" bug: two scroll containers competing for the
    // same gesture, with the inner one (the incident table's own overscroll-contain) eating
    // scroll input once it hit its own edge instead of handing it up to the page.
    <div className="flex min-h-0 flex-1 flex-col gap-5">
      <BreakdownSnapshot />
      <section>
        <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <div className={reportEyebrow}>Currently held up</div>
            <p className={reportCardSub}>
              Click a card to see it on the map and its full status — reported time, who
              reported it, and resolve.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <VehicleSearchField value={vehicleId} onChange={setVehicleId} />
            <span className="font-mono text-xs tabular-nums text-slate-400">
              <CountUp value={filteredActive.length} /> down{vehicleId ? ` (of ${active.length})` : ''}
            </span>
          </div>
        </div>
        {!activeLoaded ? (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" aria-busy="true">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="skeleton h-40 rounded-[10px]" style={{ opacity: 1 - i * 0.2 }} />
            ))}
          </div>
        ) : err ? (
          <div className={`${reportCard} ${reportCardPadSm}`}>
            <p className="text-xs text-rose-600">Breakdowns unavailable — {err}</p>
          </div>
        ) : filteredActive.length === 0 ? (
          <div className={`${reportCard} ${reportCardPadSm} flex animate-fade-in flex-col items-center gap-1.5 py-10 text-center`}>
            <AlertOctagon size={20} className="text-slate-300" />
            <p className="text-xs text-slate-400">
              {vehicleId ? 'Not currently marked down.' : 'Nobody currently marked down.'}
            </p>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {filteredActive.map((b, i) => (
              <div
                key={b.vehicle_id}
                className="animate-slide-up"
                style={{ animationDelay: `${Math.min(i, 8) * 40}ms`, animationFillMode: 'both' }}
              >
                <ActiveBreakdownCard
                  breakdown={b} onSelectVehicle={onSelectVehicle}
                  onResolved={() => setActive((cur) => cur.filter((x) => x.vehicle_id !== b.vehicle_id))}
                />
              </div>
            ))}
          </div>
        )}
      </section>

      <section className={`${reportCard} overflow-hidden`}>
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-5 py-3.5">
          <div>
            <h3 className={reportCardTitle}>All breakdowns</h3>
            <p className={`mt-0.5 ${reportCardSub}`}>Every incident recorded, newest first.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <VehicleSearchField value={vehicleId} onChange={setVehicleId} label="search vehicle" />
            <label className="flex items-center gap-1.5 text-[11px] text-slate-500">
              from
              <input
                type="date" value={dateFrom} max={dateTo || undefined}
                onChange={(e) => setDateFrom(e.target.value)}
                className="rounded-md border border-slate-200 bg-white px-2 py-1 font-mono
                           text-[11px] text-slate-700 shadow-sm transition-colors focus:border-blue-500
                           focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              />
            </label>
            <label className="flex items-center gap-1.5 text-[11px] text-slate-500">
              to
              <input
                type="date" value={dateTo} min={dateFrom || undefined}
                onChange={(e) => setDateTo(e.target.value)}
                className="rounded-md border border-slate-200 bg-white px-2 py-1 font-mono
                           text-[11px] text-slate-700 shadow-sm transition-colors focus:border-blue-500
                           focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              />
            </label>
            {(dateFrom || dateTo) && (
              <button
                type="button" onClick={() => { setDateFrom(''); setDateTo('') }}
                className="rounded-md border border-slate-200 px-2 py-1 text-[11px] text-slate-500
                           transition-colors duration-100 hover:bg-slate-50 hover:text-slate-700"
              >
                Clear
              </button>
            )}
            <span className="font-mono text-xs tabular-nums text-slate-400">
              <CountUp value={resolvedCount} /> resolved
              {medianTat != null ? ` · median TAT ${fmtDuration(medianTat)}` : ''}
            </span>
            {isAdmin && filteredLog.length > 0 && (
              <button type="button" onClick={() => void reconcileAll()} disabled={reconcilingAll}
                      title="Reconcile every row matching the current filter (not just this page) against its next geofence crossing -- read-only, changes nothing"
                      className="flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1
                                 text-[11px] text-slate-600 transition-colors duration-100 hover:bg-slate-50
                                 hover:text-slate-800 disabled:opacity-50">
                {reconcilingAll ? <Loader2 size={11} className="animate-spin" /> : <ShieldCheck size={11} />}
                Reconcile all
              </button>
            )}
          </div>
        </div>
        {!logLoaded ? (
          <div className="space-y-2 px-5 py-5" aria-busy="true">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="skeleton h-7" style={{ opacity: 1 - i * 0.13 }} />
            ))}
          </div>
        ) : filteredLog.length === 0 ? (
          <div className="animate-fade-in py-10 text-center text-xs text-slate-400">
            {vehicleId ? 'No breakdowns recorded for this vehicle.' : 'No breakdowns recorded yet.'}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead className={reportThead}>
                  <tr>
                    <th className="whitespace-nowrap bg-slate-50 px-4 py-2.5"></th>
                    <th className={reportTh}>Vehicle</th>
                    <th className={reportTh}>Category</th>
                    <th className={reportTh}>Nearest hub</th>
                    <th className={reportTh}>Distance</th>
                    <th className={reportTh}>Reported</th>
                    <th className={reportTh}>Reported by</th>
                    <th className={reportTh}>Resolved</th>
                    <th className={reportTh}>Updated by</th>
                    <th className={`${reportTh} text-right`}>TAT</th>
                    <th className={reportTh}>Status</th>
                    {isAdmin && <th className={`${reportTh} text-right`}></th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {pagedLog.map((inc, i) => (
                    <LogRow
                      key={inc.breakdown_id} inc={inc} index={i} isAdmin={isAdmin}
                      onSelectVehicle={onSelectVehicle}
                      onReplay={onReplay}
                      onSaved={(changes) => setLog((cur) => cur.map((x) =>
                        x.breakdown_id === inc.breakdown_id ? { ...x, ...changes } : x))}
                      reconciling={reconcilingIds.has(inc.breakdown_id)}
                      reconcileResult={reconcileById.get(inc.breakdown_id) ?? null}
                      reconcileError={reconcileErrById.get(inc.breakdown_id) ?? null}
                      onReconcile={() => void reconcileOne(inc.breakdown_id)}
                      onDismissReconcile={() => {
                        setReconcileById((cur) => { const m = new Map(cur); m.delete(inc.breakdown_id); return m })
                        setReconcileErrById((cur) => { const m = new Map(cur); m.delete(inc.breakdown_id); return m })
                      }}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            {logPageCount > 1 && (
              <div className="flex items-center justify-between border-t border-slate-100 px-4 py-2.5 text-[11px] text-slate-500">
                <span>
                  {logPage * LOG_PAGE_SIZE + 1}–{Math.min((logPage + 1) * LOG_PAGE_SIZE, filteredLog.length)} of {filteredLog.length}
                </span>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={logPage === 0}
                    className="rounded-md border border-slate-200 px-2 py-1 transition-colors duration-100 hover:bg-slate-50 disabled:pointer-events-none disabled:opacity-40"
                  >
                    Prev
                  </button>
                  <span className="font-mono tabular-nums">{logPage + 1} / {logPageCount}</span>
                  <button
                    type="button" onClick={() => setPage((p) => Math.min(logPageCount - 1, p + 1))}
                    disabled={logPage >= logPageCount - 1}
                    className="rounded-md border border-slate-200 px-2 py-1 transition-colors duration-100 hover:bg-slate-50 disabled:pointer-events-none disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  )
}

function fmtWhen(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  })
}

function fmtDuration(min: number): string {
  if (min < 60) return `${Math.round(min)}m`
  const h = Math.floor(min / 60)
  const m = Math.round(min % 60)
  return m > 0 ? `${h}h ${m}m` : `${h}h`
}

/** One "All breakdowns" row -- display plus, for admins, an inline edit form and a
 *  read-only reconcile check. Both live here rather than as separate dialogs: an operator
 *  scanning this table for a pattern (one vehicle, one workshop) is the one who notices
 *  something looks off, and fixing or checking it should not cost a navigation away. */
function LogRow({
  inc, index, isAdmin, onSelectVehicle, onReplay, onSaved,
  reconciling, reconcileResult, reconcileError, onReconcile, onDismissReconcile,
}: {
  inc: BreakdownLogEntry
  /** Position in the visible log, for the staggered entrance only. */
  index: number
  isAdmin: boolean
  onSelectVehicle?: (vehicleId: string) => void
  onReplay?: (breakdownId: number) => void
  onSaved: (changes: Partial<BreakdownLogEntry>) => void
  /** Reconcile state lives in BreakdownsPanel, not here -- "reconcile all" drives the same
   *  per-row state a single-row click would, so the two share one code path. */
  reconciling: boolean
  reconcileResult: BreakdownReconcile | null
  reconcileError: string | null
  onReconcile: () => void
  onDismissReconcile: () => void
}): JSX.Element {
  const [editing, setEditing] = useState(false)
  const [category, setCategory] = useState(inc.category ?? CATEGORIES[0]!)
  const [severity, setSeverity] = useState(inc.severity ?? 'major')
  const [note, setNote] = useState(inc.note ?? '')
  const [openedInput, setOpenedInput] = useState('')
  const [clearedInput, setClearedInput] = useState('')
  // Empty means "no position change" -- position is the one field that isn't always
  // known (an immobilised truck with no GPS fix), so unlike category/severity there is
  // no existing value to default these to.
  const [latInput, setLatInput] = useState('')
  const [lonInput, setLonInput] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  function startEdit(e: MouseEvent): void {
    e.stopPropagation()
    setCategory(inc.category ?? CATEGORIES[0]!)
    setSeverity(inc.severity ?? 'major')
    setNote(inc.note ?? '')
    setOpenedInput(msToLocalInputValue(new Date(inc.opened_ts).getTime()))
    setClearedInput(inc.cleared_ts ? msToLocalInputValue(new Date(inc.cleared_ts).getTime()) : '')
    setLatInput('')
    setLonInput('')
    setSaveError(null)
    setEditing(true)
  }

  /** Pre-fills the edit form's position from a geotag already fetched -- reconcile's own
   *  find, whether from the just-run check (freshest) or the last persisted one -- rather
   *  than asking an admin to type coordinates by hand. Opens the form if it wasn't
   *  already; still requires Save, same as every other field here. */
  function adoptPosition(e: MouseEvent, lat: number, lon: number): void {
    e.stopPropagation()
    if (!editing) startEdit(e)
    setLatInput(String(lat))
    setLonInput(String(lon))
  }

  async function save(e: MouseEvent): Promise<void> {
    e.stopPropagation()
    setSaving(true)
    setSaveError(null)
    try {
      const openedMs = localInputValueToMs(openedInput)
      const clearedMs = inc.cleared_ts ? localInputValueToMs(clearedInput) : null
      const lat = latInput.trim() ? Number(latInput) : undefined
      const lon = lonInput.trim() ? Number(lonInput) : undefined
      if ((lat != null && !Number.isFinite(lat)) || (lon != null && !Number.isFinite(lon))) {
        throw new Error('lat/lon must be numbers')
      }
      await editBreakdown(inc.breakdown_id, {
        category, severity, note: note.trim(),
        openedTsMs: openedMs ?? undefined,
        clearedTsMs: clearedMs ?? undefined,
        lat, lon,
      })
      onSaved({
        category, severity, note: note.trim() || null,
        opened_ts: openedMs != null ? new Date(openedMs).toISOString() : inc.opened_ts,
        cleared_ts: clearedMs != null ? new Date(clearedMs).toISOString() : inc.cleared_ts,
        edit_count: inc.edit_count + 1,
      })
      setEditing(false)
    } catch (e2) {
      setSaveError(e2 instanceof Error ? e2.message : 'could not save changes')
    } finally {
      setSaving(false)
    }
  }

  const clickable = Boolean(onSelectVehicle) && !editing
  const anim = rowEntrance(index)
  return (
    <>
      <tr className={`${anim.className} transition-colors duration-100 ${
            clickable ? 'cursor-pointer hover:bg-blue-50/40 active:bg-slate-100' : ''}`}
          style={{ animationDelay: anim.delay }}
          onClick={clickable ? () => onSelectVehicle?.(inc.vehicle_id) : undefined}>
        <td className="px-4 py-2">
          {inc.thumbnail_url ? (
            <img src={inc.thumbnail_url} alt=""
                 className="h-8 w-8 rounded object-cover ring-1 ring-slate-200 transition-transform duration-150 hover:scale-110" />
          ) : (
            <span className="block h-8 w-8 rounded bg-slate-100" />
          )}
        </td>
        <td className="px-3 py-2 font-mono font-semibold text-slate-900">{inc.vehicle_id}</td>
        <td className="px-3 py-2 text-slate-500">
          {inc.category ?? '—'}
          {inc.severity ? <span className="ml-1 text-[10.5px] text-slate-400">· {inc.severity}</span> : null}
          {inc.edit_count > 0 && (
            <span title={`edited ${inc.edit_count}×`}
                  className="ml-1 inline-flex items-center gap-0.5 text-[10px] text-slate-400">
              <History size={9} />{inc.edit_count}
            </span>
          )}
        </td>
        <td className="px-3 py-2 text-slate-500">{inc.hub_code ?? '—'}</td>
        <td className="px-3 py-2 font-mono text-slate-500">
          {inc.at_hub ? 'in hub' : inc.hub_distance_km != null ? `${inc.hub_distance_km} km` : '—'}
        </td>
        <td className="px-3 py-2 font-mono text-slate-500">{fmtWhen(inc.opened_ts)}</td>
        <td className="px-3 py-2 text-slate-500">{inc.reported_by ?? '—'}</td>
        <td className="px-3 py-2 font-mono text-slate-500">{fmtWhen(inc.cleared_ts)}</td>
        <td className="px-3 py-2 text-slate-500">{inc.updated_by ?? '—'}</td>
        <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-900">
          {inc.tat_min != null ? fmtDuration(inc.tat_min) : '—'}
        </td>
        <td className="px-3 py-2">
          {inc.active ? (
            <span className={reportPill('danger')}>
              <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-rose-500" />
              down
            </span>
          ) : (
            <span className={reportPill('ok')}>resolved</span>
          )}
        </td>
        {isAdmin && (
          <td className="px-3 py-2">
            <div className="flex items-center justify-end gap-1">
              {onReplay && (
                <button type="button"
                        onClick={(e) => { e.stopPropagation(); onReplay(inc.breakdown_id) }}
                        title="Replay on the map: reported → next different corridor site (Dhar/Jhulwania/Dhule)"
                        className="rounded p-1 text-slate-400 transition-colors duration-100 hover:bg-slate-100 hover:text-slate-600 active:scale-95">
                  <Play size={13} />
                </button>
              )}
              <button type="button"
                      onClick={(e) => { e.stopPropagation(); onReconcile() }} disabled={reconciling}
                      title={
                        `Last check: ${RECONCILE_STATUS_LABEL[inc.reconcile_status]}`
                        + (inc.reconcile_checked_ts ? ` (${fmtWhen(inc.reconcile_checked_ts)})` : '')
                        + '. Click to check against the vehicle\'s next geofence crossing after it'
                        + ' resumed moving -- read-only, changes nothing.'
                      }
                      className={`rounded p-1 transition-colors duration-100 hover:bg-slate-100 active:scale-95 disabled:opacity-50 ${
                        RECONCILE_STATUS_COLOR[inc.reconcile_status]}`}>
                {reconciling ? <Loader2 size={13} className="animate-spin" /> : <ShieldCheck size={13} />}
              </button>
              <button type="button" onClick={startEdit}
                      title="Edit this record"
                      className="rounded p-1 text-slate-400 transition-colors duration-100 hover:bg-slate-100 hover:text-slate-600 active:scale-95">
                <Pencil size={13} />
              </button>
            </div>
          </td>
        )}
      </tr>
      {reconcileResult && (
        <tr>
          <td colSpan={12} className="bg-slate-50/60 px-4 py-2 text-[11px] text-slate-600">
            <div className="flex animate-slide-up items-start justify-between gap-3">
              <div>
                <span className={`mr-1.5 font-medium ${
                  reconcileResult.matches === true ? 'text-emerald-700'
                    : reconcileResult.matches === false ? 'text-amber-700' : 'text-slate-500'
                }`}>
                  {reconcileResult.matches === true ? 'Reconciles'
                    : reconcileResult.matches === false ? 'Does not reconcile' : 'No comparison'}
                </span>
                {reconcileResult.reason}
                {reconcileResult.geotag && (
                  <span className="ml-1 text-slate-400">
                    — next tag {fmtWhen(reconcileResult.geotag.ts)}
                    {reconcileResult.geotag.geofence_name ? ` @ ${reconcileResult.geotag.geofence_name}` : ''}
                  </span>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {isAdmin && reconcileResult.matches === false && reconcileResult.geotag && (
                  <button type="button"
                          onClick={(e) => adoptPosition(e, reconcileResult.geotag!.lat, reconcileResult.geotag!.lon)}
                          className="rounded border border-amber-300 bg-white px-1.5 py-0.5 text-[10.5px]
                                     text-amber-800 hover:bg-amber-50">
                    Adopt this position
                  </button>
                )}
                <button type="button" onClick={(e) => { e.stopPropagation(); onDismissReconcile() }}
                        className="text-slate-400 hover:text-slate-600">
                  <X size={12} />
                </button>
              </div>
            </div>
          </td>
        </tr>
      )}
      {reconcileError && (
        <tr>
          <td colSpan={12} className="bg-rose-50/60 px-4 py-1.5 text-[11px] text-rose-600">
            <span className="block animate-slide-up">Reconcile failed — {reconcileError}</span>
          </td>
        </tr>
      )}
      {editing && (
        <tr onClick={(e) => e.stopPropagation()}>
          <td colSpan={12} className="bg-slate-50 px-4 py-3">
            <div className="grid animate-slide-up grid-cols-2 gap-3 sm:grid-cols-4">
              <label className="grid gap-1 text-[10.5px] text-slate-500">
                Category
                <select value={category} onChange={(e) => setCategory(e.target.value)}
                        className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[11.5px] text-slate-800">
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label className="grid gap-1 text-[10.5px] text-slate-500">
                Severity
                <select value={severity} onChange={(e) => setSeverity(e.target.value)}
                        className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[11.5px] text-slate-800">
                  {SEVERITIES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </label>
              <label className="grid gap-1 text-[10.5px] text-slate-500">
                Reported at
                <input type="datetime-local" value={openedInput}
                       onChange={(e) => setOpenedInput(e.target.value)}
                       className="rounded-md border border-slate-200 bg-white px-2 py-1 font-mono text-[11.5px] text-slate-800" />
              </label>
              {inc.cleared_ts && (
                <label className="grid gap-1 text-[10.5px] text-slate-500">
                  Resolved at
                  <input type="datetime-local" value={clearedInput}
                         onChange={(e) => setClearedInput(e.target.value)}
                         className="rounded-md border border-slate-200 bg-white px-2 py-1 font-mono text-[11.5px] text-slate-800" />
                </label>
              )}
              <label className="grid gap-1 text-[10.5px] text-slate-500">
                Latitude <span className="text-slate-400">(blank = no change)</span>
                <input type="text" inputMode="decimal" value={latInput} placeholder="unchanged"
                       onChange={(e) => setLatInput(e.target.value)}
                       className="rounded-md border border-slate-200 bg-white px-2 py-1 font-mono text-[11.5px] text-slate-800" />
              </label>
              <label className="grid gap-1 text-[10.5px] text-slate-500">
                Longitude
                <input type="text" inputMode="decimal" value={lonInput} placeholder="unchanged"
                       onChange={(e) => setLonInput(e.target.value)}
                       className="rounded-md border border-slate-200 bg-white px-2 py-1 font-mono text-[11.5px] text-slate-800" />
              </label>
              <label className="col-span-2 grid gap-1 text-[10.5px] text-slate-500 sm:col-span-4">
                Note
                <textarea value={note} rows={2} onChange={(e) => setNote(e.target.value)}
                          className="w-full resize-none rounded-md border border-slate-200 bg-white px-2 py-1 text-[11.5px] text-slate-800" />
              </label>
            </div>
            <div className="mt-2 flex items-center gap-2">
              <button type="button" onClick={save} disabled={saving}
                      className="flex items-center gap-1 rounded-md bg-slate-900 px-2.5 py-1 text-[11px] font-medium text-white hover:opacity-90 disabled:opacity-50">
                {saving ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
                Save
              </button>
              <button type="button" onClick={(e) => { e.stopPropagation(); setEditing(false) }} disabled={saving}
                      className="rounded-md px-2.5 py-1 text-[11px] text-slate-500 hover:bg-slate-100">
                Cancel
              </button>
              {saveError && <span className="text-[11px] text-rose-600">Not saved — {saveError}</span>}
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

function ActiveBreakdownCard({
  breakdown, onResolved, onSelectVehicle,
}: {
  breakdown: Breakdown
  onResolved: () => void
  onSelectVehicle?: (vehicleId: string, fallback?: { lat: number; lon: number }) => void
}): JSX.Element {
  const locate = (): void => {
    const fallback = breakdown.lat != null && breakdown.lon != null
      ? { lat: breakdown.lat, lon: breakdown.lon } : undefined
    onSelectVehicle?.(breakdown.vehicle_id, fallback)
  }
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [thumbs, setThumbs] = useState<{ photo_id: number; url?: string }[]>([])

  const loadThumbs = (): void => {
    if (breakdown.breakdown_id == null) return
    fetch(`/api/vehicle/${encodeURIComponent(breakdown.vehicle_id)}/breakdown/${breakdown.breakdown_id}/photos`)
      .then((r) => r.json() as Promise<{ photos?: { photo_id: number; url?: string }[] }>)
      .then((body) => setThumbs(body.photos ?? []))
      .catch(() => { /* thumbnails are a nice-to-have here */ })
  }
  useEffect(loadThumbs, [breakdown.breakdown_id])

  async function onFiles(files: FileList | null): Promise<void> {
    if (!files || files.length === 0 || breakdown.breakdown_id == null) return
    setUploading(true)
    setUploadError(null)
    try {
      for (const file of Array.from(files)) {
        await uploadBreakdownPhoto(breakdown.vehicle_id, breakdown.breakdown_id, file)
      }
      loadThumbs()
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : 'upload failed')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <VehicleCard
        plate={breakdown.vehicle_id}
        status="alert"
        statusLabel={breakdown.severity}
        place={breakdown.category}
        exception={{
          icon: 'power',
          tone: breakdown.severity === 'immobilised' ? 'danger' : 'warn',
          text: breakdown.note || 'no note',
        }}
        onClick={onSelectVehicle ? locate : undefined}
      />

      <div className="rounded-[10px] border border-[rgba(15,23,42,0.07)] bg-white p-2.5">
        {onSelectVehicle && (
          <button type="button" onClick={locate}
                  className="mb-2 flex w-fit items-center gap-1 rounded-md border border-slate-200 px-1.5
                             py-0.5 text-[11px] text-slate-500 hover:bg-slate-50" title="Show on map">
            <MapPin size={11} />
            Locate on map
          </button>
        )}

        <BreakdownQuickResolve vehicleId={breakdown.vehicle_id} breakdown={breakdown}
                                compact onResolved={onResolved} />

        {thumbs.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {thumbs.map((p) => (
              <a key={p.photo_id} href={p.url ?? '#'} target="_blank" rel="noreferrer"
                 className="block h-10 w-10 overflow-hidden rounded border border-slate-200 bg-slate-50">
                {p.url && <img src={p.url} alt="" className="h-full w-full object-cover" />}
              </a>
            ))}
          </div>
        )}

        <label className="mt-2 flex w-fit cursor-pointer items-center gap-1 rounded-md border border-slate-200
                          px-2 py-1 text-[10.5px] text-slate-500 hover:bg-slate-50">
          {uploading ? <Loader2 size={11} className="animate-spin" /> : <ImagePlus size={11} />}
          {uploading ? 'Uploading…' : 'Add photo'}
          <input type="file" accept="image/jpeg,image/png,image/webp,image/heic" multiple
                 className="hidden" onChange={(e) => void onFiles(e.target.files)}
                 disabled={breakdown.breakdown_id == null} />
        </label>
        {breakdown.breakdown_id == null && (
          <p className="mt-1 text-[10px] text-slate-400">
            Photos unavailable for incidents reported before this update.
          </p>
        )}
        {uploadError && <p className="mt-1 text-[10px] text-rose-600">{uploadError}</p>}
      </div>
    </div>
  )
}
